import styled from '@emotion/styled';
import { Grid, Text, Button, Space, Container, ActionIcon, Anchor } from '@mantine/core';
import { CustomSelect, CustomTextInput, StyledText } from '../../styles';
import { staticData } from './static';
import { CustomTextArea as Comment } from '../../../../common/inputs/CustomTextArea';
import { PreviewTable } from '../table/previewTable';
import { skuColumns } from '../table/skusTable';
import { plantColumns } from '../table/plantsTable';
import { IconEdit } from '@tabler/icons-react';
import { useState, useContext } from 'react';
import { DownloadIcon } from '@modulz/radix-icons';
import { keyframes } from '@emotion/react';
import { RfsContext } from '../../../../contexts/RfsContext';
import { PriorityModal } from './priority';

const Divider = () => (
  <Grid.Col span={12}>
    <Space h='sm' />
  </Grid.Col>
);

const shake = keyframes`
  0% { transform: translate(1px, 1px) rotate(0deg); }
  10% { transform: translate(-1px, -2px) rotate(-1deg); }
  20% { transform: translate(-3px, 0px) rotate(1deg); }
  30% { transform: translate(3px, 2px) rotate(0deg); }
  40% { transform: translate(1px, -1px) rotate(1deg); }
  50% { transform: translate(-1px, 2px) rotate(-1deg); }
  60% { transform: translate(-3px, 1px) rotate(0deg); }
  70% { transform: translate(3px, 1px) rotate(-1deg); }
  80% { transform: translate(-1px, -1px) rotate(1deg); }
  90% { transform: translate(1px, 2px) rotate(0deg); }
  100% { transform: translate(1px, -2px) rotate(-1deg); }
`;

const Label = ({ withAsterisk, children, withEdit, onClick, color }) => (
  <>
    <StyledText fz='md' c={color}>
      {children}{' '}
      {withAsterisk && (
        <span className='mantine-103svbs mantine-InputWrapper-required mantine-TextInput-required'>
          {' '}
          *
        </span>
      )}
      {withEdit && (
        <ActionIcon variant='subtle' onClick={onClick}>
          <IconEdit size='1rem' />
        </ActionIcon>
      )}
    </StyledText>
  </>
);

const LabelCol = styled(Grid.Col)`
  white-space: nowrap;
  overflow: hidden;
`;

const ButtonCol = styled(Grid.Col)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledLabel = styled(Label)`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  white-space: nowrap;
`;

const StyledButton = styled(Button)`
  white-space: nowrap;
  ${({ customStyle }) => customStyle && { ...customStyle }};
`;

const SelectPlantButton = ({
  label,
  color,
  onClick,
  withAsterisk = false,
  offset,
  data,
  disabled = false,
  isTP,
  style = {},
}) => {
  const columns = label === 'Select SKU' ? skuColumns : plantColumns(label.split(' ')[1], isTP);

  return (
    <Grid.Col md={6}>
      <Grid align="center">
        <LabelCol span={4}>
          <StyledLabel
            withAsterisk={withAsterisk}
            withEdit={data.length > 0}
            onClick={!disabled ? onClick : undefined}
            color={color}
            disabled={disabled}
          >
            {label}
          </StyledLabel>
        </LabelCol>

        {data.length === 0 ? (
          <ButtonCol span={6} offset={2}>
            <StyledButton
              fullWidth
              color={color}
              onClick={!disabled ? onClick : undefined}
              disabled={disabled}
              customStyle={style}
            >
              {label}
            </StyledButton>
          </ButtonCol>
        ) : (
          <Grid.Col span={12} mt="sm">
            <PreviewTable label={label} data={data} columns={columns} />
          </Grid.Col>
        )}
      </Grid>
    </Grid.Col>
  );
};

export const Form = ({ values, onChange, setPlantSkuModal, errors }) => {
  const { urgency, typeOfGoods, typeOfSku, orderType, typeOfRequest } = staticData;
  const [priorityModalOpen, setPriorityModalOpen] = useState(false);
  const { downloadFile } = useContext(RfsContext);
  const resetPlantsOnRequestTypeChange = (value, values, onChange) => {
    const newValues = {
      ...values,
      type_of_request: value,
      producing_plant_data: [],
      buying_plant_data: [],
      sales_district_of_buying_country: null,
    };

    onChange(newValues);
  };

  return (
    <>
      <Space h='md' />
      <Container style={{ margin: 0, borderLeft: '2px solid rgb(244, 224, 15' }}>
        <Text fz='md' fw={700}>
          GENERAL DETAILS
        </Text>
      </Container>
      <Space h='md' />
      <Grid>
        <Grid.Col md={6}>
          <CustomTextInput
            disabled
            value={values.requestorName}
            placeholder='Requester Name'
            label='Requester Name'
            withAsterisk
            error={errors['requestorName']}
          />
        </Grid.Col>
        <Grid.Col md={6}>
          <CustomTextInput
            disabled
            value={String(values?.requestorTeam).toUpperCase()}
            placeholder='Requester Team'
            label='Requester Team'
            withAsterisk
            error={errors['requestorTeam']}
          />
        </Grid.Col>
        <Divider />
        <Grid.Col md={6}>
          <div style={{ position: 'relative' }}>
            <Anchor
              component='button'
              onClick={() => setPriorityModalOpen(true)}
              sx={{
                textDecoration: values.urgency === 'Critical' ? 'underline' : 'none',
                fontWeight: values.urgency === 'Critical' ? 'bold' : 'normal',
                color: '#e5b611',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                position: 'absolute',
                right: 0,
                animation: values.urgency === 'Critical' ? `${shake} 1s linear` : 'none',
              }}
            >
              <DownloadIcon size={16} style={{ marginRight: '8px' }} />
              Priority Matrix
            </Anchor>
            <CustomSelect
              name='urgency'
              value={values.urgency}
              label='Urgency'
              placeholder='Select'
              data={urgency}
              onChange={(value) => onChange({ ...values, urgency: value })}
              error={errors['urgency']}
              withAsterisk
            />
          </div>
        </Grid.Col>
        <Grid.Col md={6}>
          <CustomSelect
            name='type_of_request'
            value={values.type_of_request}
            label='Type of request'
            placeholder='Select'
            data={typeOfRequest}
            onChange={(value) => resetPlantsOnRequestTypeChange(value, values, onChange)}
            error={errors['type_of_request']}
            withAsterisk
          />
        </Grid.Col>
        <Divider />

        <Grid.Col md={6}>
          <CustomSelect
            value={values.types_of_good}
            name='types_of_good'
            label='Type of Goods'
            placeholder='Select'
            data={typeOfGoods}
            withAsterisk
            onChange={(value) => {
              const updatedValues = { ...values, types_of_good: value };
              updatedValues.types_of_sku = '';
              onChange(updatedValues);
            }}
            error={errors['types_of_good']}
          />
        </Grid.Col>
        <Grid.Col md={6}>
          <CustomSelect
            value={values.types_of_sku}
            name='types_of_sku'
            label='Type of SKU'
            placeholder='Select'
            data={
              values.types_of_good === 'Finished Goods'
                ? ['Filling', 'Repack']
                : values.types_of_good === 'Road Tanker'
                ? ['Road Tanker']
                : typeOfSku
            }
            withAsterisk
            onChange={(value) => onChange({ ...values, types_of_sku: value })}
            error={errors['types_of_sku']}
          />
        </Grid.Col>

        <Divider />

        <SelectPlantButton
          label='Select SKU'
          color={errors['sku_data'] ? 'red' : 'dark'}
          onClick={() => setPlantSkuModal({ open: true, type: 'sku', title: 'Select SKU' })}
          data={values?.['sku_data']}
          withAsterisk
        />
        <SelectPlantButton
          label='Select Production Details'
          color={errors['producing_plant_data'] ? 'red' : 'dark'}
          onClick={() =>
            setPlantSkuModal({
              open: true,
              type: 'plant',
              title: 'Select Production Details',
              editMode: true,
            })
          }
          withAsterisk={true}
          offset={2}
          data={values?.['producing_plant_data']}
          isTP={values?.type_of_request === 'Transfer Pricing'}
          disabled={!values?.type_of_request}
        />
        <Divider />
        <SelectPlantButton
          label='Select Buying Details'
          color={errors['buying_plant_data'] ? 'red' : 'dark'}
          onClick={() => {
            const producingCountry = values.producing_plant_data?.[0]?.producing_country;
            setPlantSkuModal({
              open: true,
              type: 'plant',
              title: 'Select Buying Details',
              editMode: true,
              excludeCountry: producingCountry,
            });
          }}
          offset={2}
          data={values?.['buying_plant_data']}
          withAsterisk={values.type_of_request !== 'Costing'}
          isTP={values?.type_of_request === 'Transfer Pricing'}
          disabled={!values?.type_of_request}
        />

        <Divider />
        <Grid.Col md={6}>
          <CustomSelect
            value={values.stock_taking_order_type}
            name='stock_taking_order_type'
            label='Stock Taking Order Type'
            placeholder='Select'
            data={orderType}
            withAsterisk={values.type_of_request !== 'Costing'}
            onChange={(value) => onChange({ ...values, stock_taking_order_type: value })}
            error={errors['stock_taking_order_type']}
            disabled={values.type_of_request === 'Costing'}
          />
        </Grid.Col>
        <Grid.Col md={6}>
          <CustomTextInput
            value={values.sales_district_of_buying_country}
            name='sales_district_of_buying_country'
            label='Sales District Code of the Buying Country'
            placeholder='Select'
            disabled
            withAsterisk={values.type_of_request !== 'Costing'}
            onChange={(value) => onChange({ ...values, sales_district_of_buying_country: value })}
            error={errors['sales_district_of_buying_country']}
          />
        </Grid.Col>

        <Divider />
        <Grid.Col md={6}>
          <Comment
            name='comment'
            label='Any Additional Comments'
            maxLength={5000}
            onChange={(value) => onChange({ ...values, comment: value })}
            value={values?.comment || ''}
          />
        </Grid.Col>
      </Grid>

      <PriorityModal
        priorityModalOpen={priorityModalOpen}
        setPriorityModalOpen={setPriorityModalOpen}
        downloadFile={downloadFile}
      />
    </>
  );
};
